export class LazyFeatureError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Could not load this feature', { cause });
    this.name = 'LazyFeatureError';
  }
}

export function createRetryableLoader<Value>(load: () => Promise<Value>): () => Promise<Value> {
  type LoadState = { status: 'idle' } | { status: 'loading' | 'ready'; result: Promise<Value> };
  let state: LoadState = { status: 'idle' };
  return () => {
    if (state.status !== 'idle') return state.result;
    const result = Promise.resolve().then(load).then(value => {
      state = { status: 'ready', result };
      return value;
    }, (cause: unknown) => {
      state = { status: 'idle' };
      throw cause;
    });
    state = { status: 'loading', result };
    return result;
  };
}

export function createLazyFeature<Value>(load: () => Promise<Value>, onFailure?: () => void): () => Promise<Value> {
  return createRetryableLoader(async () => {
    try { return await load(); }
    catch (cause) { onFailure?.(); throw new LazyFeatureError(cause); }
  });
}
